import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { usePropertyUnderwriting } from '../hooks/usePropertyUnderwriting';
import { Tab1Financials } from '../components/PropertyView/Tab1Financials';
import { Tab2Scoring } from '../components/PropertyView/Tab2Scoring';
import { Tab3Agents } from '../components/PropertyView/Tab3Agents';
import { DealTypeSelector, type DealType } from '../components/PropertyView/DealTypeSelector';
import { Breadcrumbs } from '../components/Layout/Breadcrumbs';
import { Calculator, TrendingUp, MapPin, Loader2 } from 'lucide-react';
import api from '../services/api';
import { ZandiShowcaseButton } from '../components/PropertyView/ZandiShowcaseButton';

// Lead info type
interface LeadInfo {
  id: string;
  address: string;
  formatted_address: string;
  neighborhood: string;
  neighborhood_tier: string;
  latitude: number;
  longitude: number;
  total_units: number;
  market_value: number;
  year_built: number;
  list_price: number | null;
  lead_score: number | null;
}

export function PropertyDetail() {
  const { id } = useParams<{ id: string }>();

  // Analysis mode state - default to off-market since most properties in pipeline are off-market
  const [analysisMode, setAnalysisMode] = React.useState<'off-market' | 'on-market'>('off-market');
  const [activeTab, setActiveTab] = React.useState(0);
  const [dealType, setDealType] = React.useState<DealType>('conventional');
  // On-market listing price — pre-populated from DB when available
  const [listingPrice, setListingPrice] = React.useState<number | null>(null);

  const financingTypeForApi = dealType === 'seller_financing' ? 'seller' as const
    : dealType === 'cash' ? 'cash' as const
    : dealType === 'wholesale' ? undefined
    : 'conventional' as const;

  // Fetch lead info for address/location
  const { data: leadInfo } = useQuery({
    queryKey: ['lead', id],
    queryFn: async () => {
      const { data } = await api.get<LeadInfo>(`/leads/${id}`);
      return data;
    },
    enabled: !!id,
  });

  // Pre-populate listing price from DB when lead info loads
  React.useEffect(() => {
    if (leadInfo?.list_price && listingPrice === null) {
      setListingPrice(leadInfo.list_price);
    }
  }, [leadInfo?.list_price, listingPrice]);

  // Build underwriting options — off-market sends no purchase_price,
  // on-market sends listing price as purchase_price
  const underwritingOptions = React.useMemo(() => {
    const opts: {
      mode: 'off-market' | 'on-market';
      hold_period_years: number;
      purchase_price?: number;
      financing_type?: 'conventional' | 'seller' | 'cash';
    } = {
      mode: analysisMode,
      hold_period_years: 10,
      financing_type: financingTypeForApi,
    };
    if (analysisMode === 'on-market' && listingPrice) {
      opts.purchase_price = listingPrice;
    }
    return opts;
  }, [analysisMode, listingPrice, financingTypeForApi]);

  // Single underwriting query — offer solutions from solve_offer_price() are the primary prices
  const { data, isLoading, isFetching, error, refetch } = usePropertyUnderwriting(
    id || '',
    underwritingOptions
  );

  // Merge lead info into data for Tab1Financials
  const enrichedData = React.useMemo(() => {
    if (!data) return null;
    return {
      ...data,
      // Ensure deal_type reflects the selected analysis mode
      deal_type: analysisMode,
      // Phase 167.1: Surface lead score on property detail
      lead_score: leadInfo?.lead_score ?? null,
      // Preserve suggested offer from primary analysis
      suggested_offer_price: data?.suggested_offer_price,
      inputs: {
        ...data.inputs,
        address: leadInfo?.formatted_address || leadInfo?.address || data.inputs?.address,
        neighborhood: leadInfo?.neighborhood || data.inputs?.neighborhood,
        latitude: leadInfo?.latitude || data.inputs?.latitude,
        longitude: leadInfo?.longitude || data.inputs?.longitude,
        total_units: data.inputs?.total_units || leadInfo?.total_units || 1,
        tier: leadInfo?.neighborhood_tier || data.inputs?.tier,
        // OPA assessment for reference display (not as analysis price)
        market_value: leadInfo?.market_value || data.inputs?.market_value,
      }
    };
  }, [data, leadInfo, analysisMode]);

  const handleAnalyze = () => {
    refetch();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-obsidian">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-gold mx-auto mb-4" />
          <p className="text-stone-500">Retrieving SSOT Analysis...</p>
        </div>
      </div>
    );
  }

  if (error || !enrichedData) {
    return (
      <div className="min-h-screen bg-obsidian p-8 text-center">
        <p className="text-crimson">Error loading property analysis: {(error as Error)?.message || 'Unknown error'}</p>
      </div>
    );
  }

  const tabs = [
    { name: 'Financials', icon: Calculator },
    { name: 'Scoring', icon: TrendingUp },
    { name: 'Agents & Due Diligence', icon: MapPin },
  ];

  return (
    <div className="min-h-screen bg-obsidian pb-20">
      <div className="max-w-[1600px] mx-auto px-4 py-6">

        {/* Header */}
        <div className="mb-6">
          <Breadcrumbs
            items={[
              { label: 'Home', path: '/' },
              { label: 'Lead Pipeline', path: '/leads' },
              { label: leadInfo?.formatted_address || leadInfo?.address || `Property #${id}` },
            ]}
            className="mb-4"
          />

          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-serif text-ivory">
                {leadInfo?.formatted_address || leadInfo?.address || `Property #${id}`}
              </h1>
              <p className="text-stone-500 mt-1">
                {leadInfo?.neighborhood} • {leadInfo?.neighborhood_tier} • {leadInfo?.total_units || enrichedData.inputs?.total_units} units
              </p>
            </div>

            {/* Analysis Controls */}
            <div className="flex items-center gap-4">
              {/* On-market listing price input — only visible in on-market mode */}
              {analysisMode === 'on-market' && (
                <div className="flex items-center gap-2">
                  <label className="text-sm text-stone-500">Listing Price:</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                    <input
                      type="number"
                      value={listingPrice || ''}
                      onChange={(e) => setListingPrice(e.target.value ? Number(e.target.value) : null)}
                      placeholder="Enter listing price"
                      className="pl-7 pr-3 py-2 bg-charcoal border border-slate-dark rounded-lg text-ivory font-mono text-sm w-36 focus:border-gold focus:outline-none placeholder:text-stone-600"
                    />
                  </div>
                </div>
              )}

              {/* Zandi Showcase PDF Download */}
              <ZandiShowcaseButton
                propertyId={id || ''}
                address={leadInfo?.formatted_address || leadInfo?.address || ''}
                analysisMode={analysisMode}
                purchasePrice={analysisMode === 'on-market' ? listingPrice ?? undefined : undefined}
              />

              {/* Dev-only: Clear backend cache for this property */}
              {import.meta.env.DEV && (
                <button
                  onClick={async () => {
                    await api.post(`/v2/properties/cache/invalidate/${id}`);
                    refetch();
                  }}
                  className="px-3 py-2 text-xs font-medium text-stone-500 border border-slate-dark rounded-lg hover:text-ivory hover:border-stone-500 transition-colors"
                >
                  Clear Cache
                </button>
              )}

              {/* Analyze Button */}
              <button
                onClick={handleAnalyze}
                disabled={isFetching}
                className={`px-4 py-2 font-medium text-sm rounded-lg transition-colors ${
                  isFetching
                    ? 'bg-gold/50 text-stone-500 cursor-wait'
                    : 'bg-gold text-[#1c1917] hover:bg-gold-bright'
                }`}
              >
                {isFetching ? 'Analyzing...' : 'Analyze Deal'}
              </button>
            </div>
          </div>

          {/* Mode Toggle & Deal Type */}
          <div className="flex items-center gap-6 mt-4">
            {/* Analysis Mode */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-stone-500">Mode:</span>
              <div className="flex rounded-lg border border-slate-dark overflow-hidden">
                <button
                  onClick={() => setAnalysisMode('off-market')}
                  className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                    analysisMode === 'off-market'
                      ? 'bg-gold text-[#1c1917]'
                      : 'bg-charcoal text-stone-500 hover:text-ivory'
                  }`}
                >
                  Off-Market
                </button>
                <button
                  onClick={() => setAnalysisMode('on-market')}
                  className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                    analysisMode === 'on-market'
                      ? 'bg-gold text-[#1c1917]'
                      : 'bg-charcoal text-stone-500 hover:text-ivory'
                  }`}
                >
                  On-Market
                </button>
              </div>
            </div>

            {/* Deal Type Selector */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-stone-500">Deal Type:</span>
              <DealTypeSelector value={dealType} onChange={setDealType} />
            </div>
          </div>
        </div>

        {/* Custom Tab Navigation */}
        <div className="border-b border-slate-dark mb-6">
          <div className="flex gap-1">
            {tabs.map((tab, index) => (
              <button
                key={tab.name}
                onClick={() => setActiveTab(index)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === index
                    ? 'border-gold text-gold'
                    : 'border-transparent text-stone-500 hover:text-ivory'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.name}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 0 && <Tab1Financials data={enrichedData} dealType={dealType} />}
          {activeTab === 1 && <Tab2Scoring data={enrichedData} />}
          {activeTab === 2 && <Tab3Agents data={enrichedData} propertyId={id || String(enrichedData.property_id)} />}
        </div>
      </div>
    </div>
  );
}
